import { useState, useEffect } from "react";
import {
  X, Printer, Trash, ArrowSquareOut, DeviceMobile, User, CurrencyCircleDollar, IdentificationCard, MapPin,
  Camera, CheckCircle, Note, PencilSimple, ShoppingBag, ArrowUDownLeft, XCircle, ClockCounterClockwise, Phone, Barcode, Tag, BatteryHigh, CaretDown, PenNib,
} from "@phosphor-icons/react";
import { api, formatApiError, TRADE_IN_PAYOUT_LABELS } from "../lib/api";
import { toast } from "sonner";
import PinPromptModal from "./PinPromptModal";
import { TradeInDeviceFields } from "./TradeInDeviceFields";
import { useAuth } from "../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

export const clampPct = (v) => { const d = String(v).replace(/\D/g, "").slice(0, 3); return d === "" ? "" : String(Math.min(100, Number(d))); };

/** Trade-in detail popup: read-only by default, editable after "Edit" (PIN on save). */
const EDIT_FIELDS = ["device_make", "device_model", "device_type", "storage", "imei", "battery_health", "condition", "notes", "licence_photo_url"];
const PRICE_FIELDS = ["buy_price", "sell_price"];
const toEdits = (t) => ({
  ...Object.fromEntries(EDIT_FIELDS.map((k) => [k, t[k] || ""])),
  ...Object.fromEntries(PRICE_FIELDS.map((k) => [k, String(t[k] ?? "")])),
});
const STATUS = {
  in_stock: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "In stock", Icon: CheckCircle },
  sold:     { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Sold", Icon: ShoppingBag },
  returned: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Returned", Icon: ArrowUDownLeft },
  voided:   { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Voided", Icon: XCircle },
};
const CONDITION_TONE = {
  Excellent: "bg-emerald-50 text-emerald-700", Good: "bg-blue-50 text-blue-700", Fair: "bg-amber-50 text-amber-700",
  Poor: "bg-orange-50 text-orange-700", Faulty: "bg-rose-50 text-rose-700",
};
const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(iso).slice(0, 16); }
};
const payoutLabel = (ti) => (ti.payout_method === "cash" || !ti.payout_method ? "Cash from till" : TRADE_IN_PAYOUT_LABELS[ti.payout_method] || ti.payout_method);

function openPdf(path, errMsg) {
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("pos_token")}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((blob) => { const u = URL.createObjectURL(blob); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60_000); })
    .catch(() => toast.error(errMsg));
}

export default function TradeInDetailDrawer({ tradeInId, onClose, onChanged }) {
  const { user } = useAuth();
  const [ti, setTi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState(toEdits({}));
  const [saving, setSaving] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/trade-ins/${tradeInId}`);
        if (cancelled) return;
        setTi(r.data);
        setEdits(toEdits(r.data));
      } catch (e) { toast.error(formatApiError(e)); onClose(); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tradeInId, onClose]);

  const buildPatch = () => {
    const patch = {};
    EDIT_FIELDS.forEach((k) => { if ((edits[k] || "") !== (ti[k] || "")) patch[k] = edits[k]; });
    PRICE_FIELDS.forEach((k) => { if (Number(edits[k]) !== Number(ti[k])) patch[k] = Number(edits[k]); });
    return patch;
  };
  const dirty = ti && Object.keys(buildPatch()).length > 0;
  const canEdit = !!ti && ti.status !== "voided";
  const buyEditable = ti?.status === "in_stock";
  const buyDelta = ti ? Math.round((Number(edits.buy_price || 0) - Number(ti.buy_price || 0)) * 100) / 100 : 0;
  const set = (p) => setEdits((s) => ({ ...s, ...p }));

  const cancelEdit = () => { setEdits(toEdits(ti)); setEditing(false); };
  const save = () => {
    if (!dirty) return;
    if (!edits.device_make.trim() || !edits.device_model.trim()) { toast.error("Brand and model are required"); return; }
    setPinOpen(true);
  };
  const doSave = async (pin) => {
    setSaving(true);
    try {
      const header = user?.role === "admin" ? "X-Admin-Pin" : "X-Staff-Pin";
      const r = await api.put(`/trade-ins/${ti.id}`, buildPatch(), { headers: { [header]: pin } });
      setTi(r.data);
      setEdits(toEdits(r.data));
      setEditing(false);
      const last = (r.data.edit_history || []).slice(-1)[0];
      toast.success(last?.payout_note ? `Trade-in updated · ${last.payout_note}` : "Trade-in updated", { duration: 7000 });
      onChanged?.();
      setPinOpen(false);
    } catch (e) { toast.error(formatApiError(e)); throw e; }
    finally { setSaving(false); }
  };

  const doVoid = async () => {
    if (!window.confirm(`Void ${ti.trade_in_number}? This removes the linked product from inventory and reclaims any store credit.`)) return;
    try {
      await api.delete(`/trade-ins/${ti.id}`);
      toast.success(`${ti.trade_in_number} voided`);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onPhotoFile = (file) => {
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("Photo too large (max 1MB)"); return; }
    const r = new FileReader();
    r.onload = () => set({ licence_photo_url: r.result });
    r.readAsDataURL(file);
  };

  const st = STATUS[ti?.status] || STATUS.in_stock;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
         onClick={onClose} data-testid="ti-detail-drawer">
      <div className="relative w-full max-w-4xl max-h-[92vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4 bg-white">
          <div className="min-w-0">
            {ti && (
              <div className="flex items-center gap-3">
                <span className="font-mono text-[22px] font-bold tracking-tight text-[#1E5FEB]">{ti.trade_in_number}</span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${st.cls}`} data-testid="trade-in-status-badge">
                  <st.Icon size={12} weight="bold" /> {st.label}
                </span>
              </div>
            )}
            <div className="text-[12px] text-slate-500 mt-0.5">{ti ? `${fmtDate(ti.created_at)}${ti.created_by_name ? ` · by ${ti.created_by_name}` : ""}` : "Loading…"}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid="ti-detail-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {editing && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-[12px] font-semibold text-amber-800 flex items-center gap-2" data-testid="ti-edit-banner">
            <PencilSimple size={13} weight="bold" /> Editing trade-in details — changes need your PIN to save
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50/60 p-5 sm:p-6">
          {loading && <div className="text-[13px] text-slate-400">Loading…</div>}
          {!loading && ti && !editing && <ViewMode ti={ti} />}
          {!loading && ti && editing && (
            <EditMode ti={ti} edits={edits} set={set} buyEditable={buyEditable} buyDelta={buyDelta} onPhotoFile={onPhotoFile} />
          )}
        </div>

        {/* Footer */}
        {ti && (
          <div className="px-6 py-3.5 border-t border-slate-200 bg-white flex flex-wrap items-center gap-2">
            {!editing && (
              <>
                <FooterBtn onClick={() => openPdf(`/trade-ins/${ti.id}/receipt/pdf`, "Couldn't fetch the intake receipt")} testid="ti-detail-print"><Printer size={14} weight="bold" /> Receipt</FooterBtn>
                <FooterBtn onClick={() => openPdf(`/trade-ins/${ti.id}/label/pdf`, "Couldn't fetch the device label")} testid="ti-detail-print-label"><Barcode size={14} weight="bold" /> Label</FooterBtn>
                {ti.status === "in_stock" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="h-10 px-4 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-800 inline-flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,transform]" data-testid="ti-detail-price-tag-menu">
                        <Tag size={14} weight="bold" /> Price tag <CaretDown size={11} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-52 z-[200]">
                      <DropdownMenuItem onClick={() => openPdf(`/trade-ins/${ti.id}/display-label/pdf?layout=horizontal`, "Couldn't fetch the price tag")} data-testid="ti-detail-print-display">
                        <span className="w-8 mr-2 flex justify-center"><span className="w-7 h-4 rounded-[3px] border-2 border-slate-700" /></span> Horizontal (wide)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openPdf(`/trade-ins/${ti.id}/display-label/pdf?layout=vertical`, "Couldn't fetch the price tag")} data-testid="ti-detail-print-display-vertical">
                        <span className="w-8 mr-2 flex justify-center"><span className="w-4 h-7 rounded-[3px] border-2 border-slate-700" /></span> Vertical (tall)
                      </DropdownMenuItem>
                      <div className="px-2 pt-1.5 pb-1 mt-1 border-t border-slate-100 text-[10.5px] leading-snug text-slate-500" data-testid="ti-price-tag-print-tip">Dymo 30252 label · print at <b>Actual size / 100%</b>, not "Fit to page"</div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {ti.status === "in_stock" && (
                  <FooterBtn onClick={doVoid} testid="ti-detail-void" tone="danger"><Trash size={14} weight="bold" /> Void</FooterBtn>
                )}
              </>
            )}
            <div className="flex-1" />
            {editing ? (
              <>
                <FooterBtn onClick={cancelEdit} testid="ti-detail-cancel-edit">Cancel</FooterBtn>
                <button onClick={save} disabled={!dirty || saving} data-testid="ti-detail-save"
                        className="h-10 px-5 rounded-full bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,transform]">
                  <CheckCircle size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}
                </button>
              </>
            ) : (
              <>
                <FooterBtn onClick={onClose} testid="ti-detail-close-footer">Close</FooterBtn>
                {canEdit && (
                  <button onClick={() => setEditing(true)} data-testid="ti-detail-edit"
                          className="h-10 px-5 rounded-full bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8] inline-flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,transform]">
                    <PencilSimple size={14} weight="bold" /> Edit details
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <PinPromptModal
        open={pinOpen}
        title="Save trade-in changes"
        description={buyEditable && Math.abs(buyDelta) > 0.004 ? `Buy price changes by $${buyDelta.toFixed(2)} — the payout will be adjusted.` : "Enter your PIN to save the changes."}
        confirmLabel="SAVE CHANGES"
        statusEndpoint={user?.role === "admin" ? "/users/me/pin-status" : "/staff-pin/status"}
        onClose={() => setPinOpen(false)}
        onConfirm={doSave}
      />
    </div>
  );
}

function ViewMode({ ti }) {
  const margin = Number(ti.sell_price || 0) - Number(ti.buy_price || 0);
  const pct = Number(ti.buy_price) > 0 ? Math.round((margin / Number(ti.buy_price)) * 100) : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
      {/* Device hero */}
      <div className="md:col-span-12 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4" data-testid="ti-view-device">
        <div className="w-14 h-14 rounded-2xl bg-[#1E5FEB] text-white flex items-center justify-center shrink-0 shadow-[0_8px_20px_-8px_rgba(30,95,235,0.7)]">
          <DeviceMobile size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{ti.device_type || "Device"}</div>
          <div className="text-[20px] font-bold tracking-tight text-slate-900 truncate">
            {[ti.device_make, ti.device_model].filter(Boolean).join(" ") || "—"}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {ti.storage && <span className="px-2 py-0.5 rounded-md bg-slate-900 text-white text-[11px] font-bold" data-testid="ti-view-storage">{ti.storage}</span>}
            {ti.condition && <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${CONDITION_TONE[ti.condition] || "bg-slate-100 text-slate-700"}`} data-testid="ti-view-condition">{ti.condition}</span>}
            {ti.battery_health && <span className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-bold text-slate-700 inline-flex items-center gap-1" data-testid="ti-view-battery"><BatteryHigh size={13} weight="bold" />{ti.battery_health}%</span>}
            <span className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-mono text-slate-600" data-testid="ti-view-imei">{ti.imei ? `IMEI ${ti.imei}` : "No IMEI"}</span>
          </div>
        </div>
        <div className="sm:text-right">
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">Sell price</div>
          <div className="text-[26px] font-black tracking-tight font-mono text-[#1E5FEB] tabular-nums" data-testid="ti-view-sell-price">{money(ti.sell_price)}</div>
        </div>
      </div>

      {/* Pricing */}
      <Card className="md:col-span-7" icon={CurrencyCircleDollar} title="Pricing & payout">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Bought for" value={money(ti.buy_price)} testid="ti-view-buy-price" />
          <Stat label="Sell price" value={money(ti.sell_price)} />
          <Stat label="Margin" value={money(margin)} tone={margin >= 0 ? "text-emerald-700" : "text-rose-700"}
                sub={pct != null ? `${pct}%` : null} />
        </div>
        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3.5 py-2.5 flex items-center justify-between gap-3" data-testid="ti-view-payout">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">Paid to customer by</div>
            <div className="text-[13.5px] font-semibold text-slate-900">{payoutLabel(ti)}</div>
          </div>
          {ti.payout_reference && <div className="font-mono text-[12px] text-slate-600 text-right">Ref {ti.payout_reference}</div>}
        </div>
        {ti.status === "sold" && ti.linked_sale_id && (
          <a href={`/sales/${ti.linked_sale_id}`} className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#1E5FEB] hover:underline">
            View sale <ArrowSquareOut size={12} weight="bold" />
          </a>
        )}
        {ti.status === "voided" && ti.voided_reason && <div className="mt-3 text-[12px] text-rose-700">Void reason: {ti.voided_reason}</div>}
      </Card>

      {/* Customer */}
      <Card className="md:col-span-5" icon={User} title="Customer (at intake)">
        <div className="text-[15px] font-bold text-slate-900">{ti.customer_name_snapshot || "—"}</div>
        <div className="mt-2 space-y-1.5 text-[12.5px] text-slate-600">
          <Line icon={Phone}>{ti.customer_phone_snapshot || "—"}</Line>
          <Line icon={IdentificationCard}>{ti.customer_licence_snapshot || "—"}{ti.customer_licence_state_snapshot ? ` (${ti.customer_licence_state_snapshot})` : ""}</Line>
          <Line icon={MapPin}>{ti.customer_address_snapshot || "—"}</Line>
        </div>
      </Card>

      {ti.notes && (
        <Card className="md:col-span-12" icon={Note} title="Notes">
          <div className="text-[13px] text-slate-700 whitespace-pre-wrap" data-testid="ti-view-notes">{ti.notes}</div>
        </Card>
      )}

      <Card className="md:col-span-6" icon={Camera} title="Licence photo">
        {ti.licence_photo_url
          ? <img src={ti.licence_photo_url} alt="Licence" className="w-full max-h-44 object-contain rounded-lg border border-slate-200 bg-slate-50" />
          : <div className="text-[12.5px] text-slate-400">No photo on file.</div>}
      </Card>

      <Card className="md:col-span-6" icon={PenNib} title="Customer signature">
        {ti.signature_png ? (
          <div data-testid="ti-view-signature">
            <img src={ti.signature_png} alt="Customer signature" className="w-full max-h-36 object-contain rounded-lg border border-slate-200 bg-white" />
            <div className="mt-1.5 text-[11.5px] text-slate-500">
              Signed {ti.signed_at ? fmtDate(ti.signed_at) : ""}{ti.signature_method === "wacom_stu" ? " · on Wacom pad" : " · on screen"}
            </div>
          </div>
        ) : <div className="text-[12.5px] text-slate-400">Not signed (intaken before signatures were required).</div>}
      </Card>

      <Card className="md:col-span-6" icon={ClockCounterClockwise} title="Edit history">
        {(ti.edit_history || []).length === 0 ? (
          <div className="text-[12.5px] text-slate-400">No changes since intake.</div>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-1.5 space-y-3" data-testid="ti-detail-history">
            {[...ti.edit_history].reverse().map((h, i) => (
              <li key={`${h.at}-${i}`} className="pl-4 relative">
                <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[#1E5FEB] ring-4 ring-white" />
                <div className="text-[12px] font-semibold text-slate-900">{fmtDate(h.at)} · {h.by}</div>
                <div className="text-[12px] text-slate-600">
                  {Object.entries(h.changes || {}).map(([k, c]) => `${k.replace(/_/g, " ")}: ${c.from ?? "—"} → ${c.to}`).join(" · ")}
                </div>
                {h.payout_note && <div className="text-[12px] font-semibold text-amber-700">{h.payout_note}</div>}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function EditMode({ ti, edits, set, buyEditable, buyDelta, onPhotoFile }) {
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Card icon={DeviceMobile} title="Device">
        <TradeInDeviceFields value={edits} onChange={set} idPrefix="ti-detail" />
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Field label="IMEI / Serial">
            <input value={edits.imei} onChange={(e) => set({ imei: e.target.value })} className={`${INPUT} font-mono`} data-testid="ti-detail-imei" />
          </Field>
          <Field label="Battery health %">
            <input value={edits.battery_health || ""} onChange={(e) => set({ battery_health: clampPct(e.target.value) })} placeholder="e.g. 87 (optional)" inputMode="numeric" className={INPUT} data-testid="ti-detail-battery" />
          </Field>
          <Field label="Notes">
            <input value={edits.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Face ID ok, small scratch…" className={INPUT} data-testid="ti-detail-notes" />
          </Field>
        </div>
      </Card>

      <Card icon={CurrencyCircleDollar} title="Pricing">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={buyEditable ? "Bought for" : "Bought for (locked after sale)"}>
            <MoneyInput value={edits.buy_price} onChange={(v) => set({ buy_price: v })} disabled={!buyEditable} testid="ti-detail-buy-price" />
          </Field>
          <Field label="Sell price">
            <MoneyInput value={edits.sell_price} onChange={(v) => set({ sell_price: v })} testid="ti-detail-sell-price" />
          </Field>
        </div>
        {buyEditable && Math.abs(buyDelta) > 0.004 && (
          <div className="mt-3 text-[12.5px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="ti-detail-payout-adjust">
            {(ti.payout_method || "cash") === "cash"
              ? (buyDelta > 0 ? `Pay the customer $${buyDelta.toFixed(2)} more — logged as a till cash-out` : `Collect $${(-buyDelta).toFixed(2)} back — logged as a till cash-in`)
              : ti.payout_method === "store_credit"
                ? `Store credit will ${buyDelta > 0 ? "increase" : "decrease"} by $${Math.abs(buyDelta).toFixed(2)}`
                : buyDelta > 0 ? `Send $${buyDelta.toFixed(2)} more by ${TRADE_IN_PAYOUT_LABELS[ti.payout_method]}` : `Customer owes $${(-buyDelta).toFixed(2)} back`}
          </div>
        )}
      </Card>

      <Card icon={Camera} title="Licence photo">
        <div className="flex items-start gap-3">
          {edits.licence_photo_url
            ? <img src={edits.licence_photo_url} alt="Licence" className="w-36 h-24 object-cover rounded-lg border border-slate-200" />
            : <div className="text-[12.5px] text-slate-400">No photo on file.</div>}
          <div className="space-y-1.5">
            <input type="file" accept="image/*" onChange={(e) => onPhotoFile(e.target.files?.[0])} className="text-[12px]" data-testid="ti-detail-photo-input" />
            {edits.licence_photo_url && <button onClick={() => set({ licence_photo_url: "" })} className="block text-[12px] text-rose-700 hover:underline">Remove photo</button>}
          </div>
        </div>
      </Card>
    </div>
  );
}

const INPUT = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] focus:border-[#1E5FEB] focus:ring-2 focus:ring-[#1E5FEB]/15 outline-none transition-[border-color,box-shadow]";

function Card({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-[border-color,box-shadow] duration-150 hover:border-blue-200 hover:shadow-md ${className}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3 flex items-center gap-2">
        <Icon size={14} weight="bold" className="text-[#1E5FEB]" /> {title}
      </div>
      {children}
    </section>
  );
}
function Stat({ label, value, tone = "text-slate-900", sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-[17px] font-bold font-mono tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="inline-block mt-0.5 text-[10.5px] font-bold font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{sub}</div>}
    </div>
  );
}
function Line({ icon: Icon, children }) {
  return <div className="flex items-start gap-2"><Icon size={13} weight="bold" className="text-slate-400 mt-0.5 shrink-0" /><span>{children}</span></div>;
}
function Field({ label, children }) {
  return <label className="block"><div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5">{label}</div>{children}</label>;
}
function MoneyInput({ value, onChange, disabled, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
      <input type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
             className={`${INPUT} pl-7 tabular-nums disabled:bg-slate-50 disabled:text-slate-400`} data-testid={testid} />
    </div>
  );
}
function FooterBtn({ onClick, children, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`h-10 px-4 rounded-full border text-[13px] font-semibold inline-flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,transform] ${
              tone === "danger" ? "border-rose-200 text-rose-700 bg-white hover:bg-rose-50" : "border-slate-200 text-slate-800 bg-white hover:bg-slate-50"
            }`}>
      {children}
    </button>
  );
}
